import { capitalizeFirstLetter } from '../../../utils';
import { formatDate, getBrowserTimezone, relative_dates } from '../../../utils/date';
import { BulkOperationFilters, BulkOperationModel, BulkOperationType } from './types';

// Clean up query for bulk operations by removing pagination fields
export const cleanupQueryForBulkOperation = (query: BulkOperationFilters): BulkOperationFilters => {
  const cleaned = { ...query };
  // Remove pagination fields that shouldn't be part of bulk operations
  if ('next' in cleaned) delete cleaned.next;
  if ('prev' in cleaned) delete cleaned.prev;
  return cleaned;
};

// Determine API method based on operation type
export const getApiMethod = (operation_type: BulkOperationType, model?: BulkOperationModel) => {
  if (operation_type === 'cancel') {
    return 'cancelEvents';
  }
  return model || 'events';
};

// Ensure date filters are present with default values
export const ensureDateFilters = (filters: BulkOperationFilters): BulkOperationFilters => {
  const hasDate = 'date' in filters && filters.date && filters.date.min && filters.date.max;
  const yesterdayDate = relative_dates.yesterday.convert(new Date());

  return {
    ...filters,
    date: hasDate
      ? filters.date
      : {
          min: yesterdayDate.min.toISOString(),
          max: yesterdayDate.max.toISOString(),
        },
  };
};

// Calculate progress percentage for operations that need it
export const calculateProgressPercentage = (
  completedCount: number,
  estimatedCount: number,
): number => {
  if (!estimatedCount || estimatedCount === 0) return 0;
  return Math.round((completedCount / estimatedCount) * 100);
};

// Check if filters are empty (no meaningful filters applied)
export const hasNoFilters = (filters: BulkOperationFilters): boolean => {
  const cleaned = cleanupQueryForBulkOperation(filters);
  const values = Object.values(cleaned);

  return (
    values.filter((v) => {
      if ((Array.isArray(v) && v.length === 0) || v === null || v === undefined) {
        return false;
      }
      if (typeof v === 'object') {
        return Object.values(v).some((subV) => !!subV);
      }
      return !!v;
    }).length === 0
  );
};

// Format date for display in breakdown
export const prettyPrintDate = (
  date: string | number | Date,
  timezone = getBrowserTimezone(),
): string => formatDate(date, 'MMM d, h:mm a', timezone);

// Get operation-specific labels based on type and model
export const getOperationLabels = (type: BulkOperationType, model?: BulkOperationModel) => {
  switch (type) {
    case 'retry':
      return {
        label: 'Bulk retry',
        label_plural: 'Bulk retries',
        model_label: model ? getModelLabel(model) : 'items',
        action_label: 'Retry all',
        abort_label: 'Abort bulk retry',
        conditions_label: 'Conditions',
        ongoing_label: 'Ongoing Retries',
        history_label: 'History',
        view_label: `View ${model ? getModelLabel(model) : 'items'}`,
        success_message: `${model ? capitalizeFirstLetter(getModelLabel(model)) : 'Items'} queued for retry`,
        error_message: `${model ? capitalizeFirstLetter(getModelLabel(model)) : 'Items'} failed to queue for retry`,
        dialog_title: `Bulk retry ${model ? getModelLabel(model) : 'items'}`,
        dialog_message: `Executing this bulk action will retry all eligible ${model ? getModelLabel(model) : 'items'} with the below conditions. Are you sure you'd like to proceed?`,
        dialog_cancel_label: `No, don't bulk retry`,
        dialog_submit_label: `Bulk retry ${model ? getModelLabel(model) : 'items'}`,
      };
    case 'cancel':
      return {
        label: 'Bulk cancel',
        label_plural: 'Bulk cancels',
        model_label: 'events',
        action_label: 'Cancel all',
        abort_label: 'Abort bulk cancel',
        conditions_label: 'Conditions',
        ongoing_label: 'Ongoing Cancellations',
        history_label: 'History',
        view_label: 'View events',
        success_message: 'Events queued for cancellation',
        error_message: 'Failed to queue events for cancellation',
        dialog_title: 'Bulk cancel events',
        dialog_message:
          "Executing this bulk action will cancel all eligible deliveries for events with the below conditions. Are you sure you'd like to proceed?",
        dialog_cancel_label: "No, don't bulk cancel",
        dialog_submit_label: 'Bulk cancel events',
      };
  }
};

// Get model-specific label for retry operations
const getModelLabel = (model: BulkOperationModel): string => {
  switch (model) {
    case 'events':
      return 'events';
    case 'requests':
      return 'requests';
    case 'ignored_events':
      return 'requests with ignored events';
    default:
      return 'items';
  }
};

// Get navigation path for viewing operation results
export const getNavigationPath = (
  operation_type: BulkOperationType,
  model?: BulkOperationModel,
): { path: string; search?: string } => {
  switch (operation_type) {
    case 'retry': {
      if (!model) return { path: '/events' };
      const navigation = {
        events: { path: '/events', search: `?bulk_retry_id=${''}` },
        requests: { path: '/requests', search: `?bulk_retry_id=${''}` },
        ignored_events: { path: '/requests', search: `?bulk_retry_id=${''}` },
      };
      return navigation[model] || { path: '/events' };
    }
    case 'cancel':
      return { path: '/events', search: `?bulk_retry_id=${''}` };
  }
};

// Check if operation has invalid statuses for cancellation
export const hasInvalidStatuses = (filters: BulkOperationFilters): boolean => {
  if (!('status' in filters) || !filters.status || !Array.isArray(filters.status)) {
    return false;
  }

  const invalid_statuses = ['successful', 'discarded'];
  return filters.status.some((status) => invalid_statuses.includes(status));
};

// Check if there are ongoing identical operations
export const hasOngoingIdenticalOperation = (
  ongoing_operations: any[],
  query: BulkOperationFilters,
): boolean => {
  return ongoing_operations.some((op) => JSON.stringify(op.query) === JSON.stringify(query));
};

// Re-export BULK_OPERATION_CONFIGS from types.ts for component imports
export { BULK_OPERATION_CONFIGS } from './types';
